import classNames from 'classnames';
import { ComponentProps, FocusEventHandler, forwardRef, useState } from 'react';

import HiddenField from '../HiddenField/HiddenField';
import TextField from '../TextField/TextField';

import useLocalizeNumber from './hooks/useLocalizeNumber';
import LocalizedNumber from './types/LocalizedNumber';

import styles from './NumericField.module.css';

type NumericFieldProps = {
    minDecimals?: number;
    maxDecimals?: number;
} & Omit<ComponentProps<typeof TextField>, 'inputMode' | 'pattern'>;

const NumericField = forwardRef<HTMLInputElement, NumericFieldProps>(
    (
        {
            minDecimals = 0,
            maxDecimals = 0,
            inputClassName,
            name,
            value,
            defaultValue,
            onChange,
            ...textFieldProps
        },
        ref
    ) => {
        const classes = classNames(inputClassName, styles.Input);
        const { parseInternational, parseLocalized } = useLocalizeNumber({
            minDecimals: minDecimals,
            maxDecimals: maxDecimals,
        });

        const [localValue, setLocalValue] = useState<LocalizedNumber>(() => {
            const valueToUse = String(
                (typeof value !== 'undefined' ? value : defaultValue) ?? ''
            );

            return parseInternational(valueToUse, { format: true });
        });

        const handleChange = (value: string) => {
            const localizedValue = parseLocalized(value, {
                format: false,
            });

            setLocalValue(localizedValue);

            onChange && onChange(localizedValue.floatAsString);
        };

        const handleBlur: FocusEventHandler<HTMLInputElement> = (event) => {
            const localizedValue = parseLocalized(event.target.value, {
                format: true,
            });

            setLocalValue(localizedValue);

            if (localizedValue.float !== localValue.float) {
                onChange && onChange(localizedValue.floatAsString);
            }
        };

        return (
            <>
                <TextField
                    ref={ref}
                    inputMode={maxDecimals > 0 ? 'decimal' : 'numeric'}
                    pattern="[\s0-9.,\-]*"
                    inputClassName={classes}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={localValue.localized}
                    {...textFieldProps}
                />
                <HiddenField name={name} value={localValue.floatAsString} />
            </>
        );
    }
);

export default NumericField;
